import { Fragment, useState } from 'react';
import { Eye, ChevronDown, ChevronUp, User, Monitor, Globe, Building2 } from 'lucide-react';
import type { AuditLogEntry } from '@yawi/types';
import { Card } from '@/core/ui/components';

interface AuditLogTableProps {
  logs: AuditLogEntry[];
  isLoading: boolean;
  actors: { id: string; name: string }[];
}

const eventColors: Record<string, string> = {
  ADMIN_ACTION: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
  SUPER_ADMIN_TENANT_ACCESS: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  LOGIN_SUCCESS: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  LOGIN_FAILED: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  ACCOUNT_LOCKED: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
  TOKEN_REUSE_DETECTED: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400',
};

const NEUTRAL = 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400';
const OK = 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400';
const FAIL = 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400';

/** ADMIN_ACTION -> "Admin Action" */
export const formatEventType = (type: string) =>
  type.toLowerCase().replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatTimestamp = (timestamp: string) => {
  const date = new Date(timestamp);
  const diff = Date.now() - date.getTime();

  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)} minutes ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)} hours ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)} days ago`;

  return date.toLocaleString();
};

/** Qué se tocó: ruta del ADMIN_ACTION o el mensaje del evento de seguridad */
export const describeEvent = (log: AuditLogEntry) => {
  const d = log.details ?? {};
  if (log.eventType === 'ADMIN_ACTION') {
    return { title: String(d.resource ?? ''), subtitle: `${d.method ?? ''} ${d.path ?? ''}`.trim() };
  }
  return { title: log.message ?? '', subtitle: '' };
};

function ResultBadge({ log }: { log: AuditLogEntry }) {
  const statusCode = log.details?.statusCode;
  if (typeof statusCode === 'number') {
    return (
      <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${statusCode < 400 ? OK : FAIL}`}>
        {statusCode}
      </span>
    );
  }
  return (
    <span
      className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
        log.severity === 'INFO' ? NEUTRAL : FAIL
      }`}
    >
      {log.severity}
    </span>
  );
}

export function AuditLogTable({ logs, isLoading, actors }: AuditLogTableProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const actorNames = new Map(actors.map((a) => [a.id, a.name]));

  if (isLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"></div>
        </div>
      </Card>
    );
  }

  if (logs.length === 0) {
    return (
      <Card>
        <div className="text-center py-12">
          <Eye className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-800 dark:text-white/90">
            No audit logs found
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Try adjusting your filters or wait for new activity
          </p>
        </div>
      </Card>
    );
  }

  const headerClass =
    'px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider';

  return (
    <Card>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-800">
              <th className={headerClass}>Timestamp</th>
              <th className={headerClass}>Actor</th>
              <th className={headerClass}>Event</th>
              <th className={headerClass}>Resource</th>
              <th className={headerClass}>Result</th>
              <th className={`${headerClass} text-right`}>Details</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-800">
            {logs.map((log) => {
              const { title, subtitle } = describeEvent(log);
              const actorName =
                (log.userId && actorNames.get(log.userId)) || log.userEmail || log.userId || 'System';
              return (
                <Fragment key={log.id}>
                  <tr
                    className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer"
                    onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                  >
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-800 dark:text-white/90">
                        {formatTimestamp(log.createdAt)}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {new Date(log.createdAt).toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                          <User className="w-4 h-4 text-gray-500" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-800 dark:text-white/90">{actorName}</p>
                          {log.userEmail && log.userEmail !== actorName && (
                            <p className="text-xs text-gray-500 dark:text-gray-400">{log.userEmail}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                          eventColors[log.eventType] || NEUTRAL
                        }`}
                      >
                        {formatEventType(log.eventType)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-800 dark:text-white/90 capitalize">{title}</div>
                      {subtitle && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 font-mono">{subtitle}</div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <ResultBadge log={log} />
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800">
                        {expandedId === log.id ? (
                          <ChevronUp className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                    </td>
                  </tr>
                  {expandedId === log.id && (
                    <tr>
                      <td colSpan={6} className="px-6 py-4 bg-slate-50 dark:bg-slate-800/50">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          {log.ipAddress && (
                            <div className="flex items-center gap-2 text-sm">
                              <Globe className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-500 dark:text-gray-400">IP Address:</span>
                              <span className="text-gray-800 dark:text-white/90">{log.ipAddress}</span>
                            </div>
                          )}
                          {log.userAgent && (
                            <div className="flex items-center gap-2 text-sm">
                              <Monitor className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-500 dark:text-gray-400">User Agent:</span>
                              <span className="text-gray-800 dark:text-white/90 truncate max-w-xs">
                                {log.userAgent}
                              </span>
                            </div>
                          )}
                          {log.tenantId && (
                            <div className="flex items-center gap-2 text-sm">
                              <Building2 className="w-4 h-4 text-gray-400" />
                              <span className="text-gray-500 dark:text-gray-400">Tenant:</span>
                              <span className="text-gray-800 dark:text-white/90 font-mono text-xs">
                                {log.tenantId}
                              </span>
                            </div>
                          )}
                        </div>
                        {log.details && Object.keys(log.details).length > 0 && (
                          <pre className="mt-4 font-mono text-xs text-gray-800 dark:text-white/90 overflow-x-auto bg-white dark:bg-gray-900 rounded-lg p-3">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
